import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { createChallenge, createChallengeOnRallyeStation } from '../../api/content';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { ChallengeType, MultilingualString, ScoringRules } from '../../api/types';
import ErrorAlert from '../../components/ErrorAlert';
import MultilingualField from '../../components/MultilingualField';
import ScoringRulesEditor from '../../components/ScoringRulesEditor';
import BackLink from '../../components/BackLink';
import { pruneScoringRules } from '../../utils/scoringRules';

const CHALLENGE_TYPES: ChallengeType[] = ['photo', 'video', 'text', 'qr_scan', 'quiz', 'creative'];

const ChallengeCreate: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation('content');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When launched from the rallye composition flow, the new challenge is
  // created AND matched to the station within the rallye in one call
  // (createChallengeOnRallyeStation).
  const rallyeId = searchParams.get('rallyeId');
  const rallyeStationId = searchParams.get('rallyeStationId');
  const composing = !!rallyeId && !!rallyeStationId;

  const [challengeType, setChallengeType] = useState<ChallengeType | null>(null);
  const [title, setTitle] = useState<MultilingualString>({});
  const [instructions, setInstructions] = useState<MultilingualString>({});
  const [scoringRules, setScoringRules] = useState<ScoringRules>({ max_points: 10 });
  const [duration, setDuration] = useState('');
  const [solution, setSolution] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgSlug = user?.organization_slug;
  const canSubmit =
    !!orgSlug &&
    challengeType !== null &&
    !!title.de?.trim() &&
    !!instructions.de?.trim() &&
    (scoringRules.max_points ?? 0) >= 1 &&
    !submitting;

  const backTo = composing ? `/rallyes/${rallyeId}` : '/challenges';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgSlug || !challengeType) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        challenge_type: challengeType,
        title,
        instructions,
        scoring_rules: scoringRules,
        time_estimate_minutes: duration ? parseInt(duration, 10) : null,
        suggested_solution: solution.trim() || null,
      };
      if (composing && rallyeId && rallyeStationId) {
        await createChallengeOnRallyeStation(orgSlug, rallyeId, rallyeStationId, body);
        navigate(`/rallyes/${rallyeId}`);
      } else {
        const challenge = await createChallenge(orgSlug, body);
        navigate(`/challenges/${challenge.id}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <BackLink to={backTo} label={t('challenge.detail.backToList')} />
      <Typography variant="h2" component="h1" gutterBottom>
        {t('challenge.create.title')}
      </Typography>
      {error && <ErrorAlert message={error} />}
      <Stack spacing={3} sx={{ mt: 3 }}>
        <Box>
          <Typography variant="subtitle1" component="h2" gutterBottom>
            {t('challenge.create.selectType')}
          </Typography>
          <ToggleButtonGroup
            value={challengeType}
            exclusive
            onChange={(_, next: ChallengeType | null) => {
              setChallengeType(next);
              // Switching type silently drops the previous type's scoring keys
              // (#459). Without this, configuring a quiz and then saving as text
              // carries auto_approve along, where it does nothing — the form
              // claims the task scores itself and a coordinator waits forever.
              setScoringRules((rules) => pruneScoringRules(next, rules));
            }}
            aria-label={t('challenge.create.selectType')}
          >
            {CHALLENGE_TYPES.map((type) => (
              <ToggleButton key={type} value={type}>
                {t(`challenge.types.${type}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <MultilingualField
          value={title}
          onChange={setTitle}
          label={t('challenge.create.titleLabel')}
          required
        />

        <MultilingualField
          value={instructions}
          onChange={setInstructions}
          label={t('challenge.create.instructionsLabel')}
          required
          richText
        />

        <TextField
          label={t('challenge.create.durationLabel')}
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          type="number"
          slotProps={{ htmlInput: { min: 0 } }}
          sx={{ maxWidth: 240 }}
        />

        {challengeType !== null && (
          <ScoringRulesEditor
            value={scoringRules}
            onChange={setScoringRules}
            challengeType={challengeType}
          />
        )}

        <TextField
          label={t('challenge.create.solutionLabel')}
          value={solution}
          onChange={(e) => setSolution(e.target.value)}
          multiline
          minRows={2}
          helperText={t('challenge.create.solutionHelp')}
        />

        <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate(backTo)} disabled={submitting}>
            {t('actions.cancel', { ns: 'common' })}
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {t('challenge.create.submit')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ChallengeCreate;
